/**
 * OppExpService
 *
 * This service handles all opportunity expense-related operations in the application.
 * It provides methods for managing opportunity expenses, including creation, retrieval,
 * updating, and deletion of opportunity expense entries. The service supports operations
 * for both individual matters and system-wide opportunity expense management.
 *
 * @class
 */

import {
  pathOppExpenses,
  pathOppExpensesMatterId,
  pathOppExpensesOpportunityId,
} from '@frontend/__settings__/septSoftwarePaths';
import HttpService from './HttpService';
import {
  AddOppExpenseRequest,
  AddOppExpenseResponse,
  DeleteOppExpenseResponse,
  GetAllOppExpensesResponse,
  GetOppExpensesByMatterResponse,
  UpdateOppExpenseRequest,
  UpdateOppExpenseResponse,
} from '@frontend/Models/OpportunityExpenses/OpportunityExpensesRequestResponseModels';
const http = new HttpService();

class OppExpService {
  /**
   * Retrieves all opportunity expenses from the system
   *
   * @returns {Promise<Array>} Array of opportunity expense objects
   * @throws {Error} If the API request fails
   */
  static async getAllOppExpenses(): Promise<GetAllOppExpensesResponse> {
    const data = await http.get<GetAllOppExpensesResponse>(pathOppExpenses);
    return data;
  }

  /**
   * Retrieves opportunity expenses for a specific matter
   *
   * @param {string} matterId - ID of the matter
   * @returns {Promise<Array>} Array of opportunity expenses for the matter
   * @throws {Error} If the API request fails
   */
  static async getOppExpensesByMatter(
    matterId: string
  ): Promise<GetOppExpensesByMatterResponse> {
    const data = await http.get<GetOppExpensesByMatterResponse>(
      pathOppExpensesMatterId(matterId)
    );
    return data;
  }

  /**
   * Adds a new opportunity expense
   *
   * @param {Object} expenseData - Opportunity expense data
   * @param {string} expenseData.matterId - ID of the matter
   * @param {string} expenseData.name - Name of the expense
   * @param {number} expenseData.amount - Amount of the expense
   * @param {string} [expenseData.description] - Description of the expense
   *
   * @returns {Promise<Object>} Created opportunity expense object
   * @throws {Error} If the API request fails
   */
  static async addOppExpense(
    expenseData: AddOppExpenseRequest
  ): Promise<AddOppExpenseResponse> {
    const data = await http.post<AddOppExpenseResponse>(
      pathOppExpenses,
      expenseData
    );
    return data;
  }
  /**
   * Updates an existing opportunity expense
   *
   * @param {string} oppId - ID of the opportunity expense
   * @param {Object} expenseData - Updated opportunity expense data
   * @returns {Promise<Object>} Updated opportunity expense object
   * @throws {Error} If the API request fails
   */
  static async updateOppExpense(
    oppId: string,
    expenseData: UpdateOppExpenseRequest
  ): Promise<UpdateOppExpenseResponse> {
    const data = await http.put<UpdateOppExpenseResponse>(
      pathOppExpensesOpportunityId(oppId),
      expenseData
    );
    return data;
  }

  /**
   * Deletes an opportunity expense
   *
   * @param {string} oppId - ID of the opportunity expense to delete
   * @returns {Promise<Object>} Response indicating success or failure
   * @throws {Error} If the API request fails
   */
  static async deleteOppExpense(
    oppId: string
  ): Promise<DeleteOppExpenseResponse> {
    const data = await http.delete<DeleteOppExpenseResponse>(
      pathOppExpensesOpportunityId(oppId)
    );
    return data;
  }
}

export default OppExpService;
